import { z } from 'zod';
import { aanbodSchema } from './aanbod';
import { beoordelingSchema } from './beoordeling';
import { isoDatumTijdSchema } from './common';
import { energielabelSchema } from './energielabel';
import { faseSchema } from './fase';
import { woningstatusSchema } from './historie';

/**
 * One house for sale, exactly the fields of the normalised source file plus the
 * database's own id, its dataset and the generated price per m2.
 *
 * Money is in whole euros, area in whole m2. `lat`/`lng` are null when the
 * address could not be geocoded: such a listing belongs in the table, not on the map.
 */
export const listingSchema = z.object({
  id: z.uuid(),
  datasetId: z.uuid(),

  bronId: z.string().min(1),
  bron: z.string().min(1),
  bronUrl: z.url().nullable(),
  foto: z.url().nullable(),

  adres: z.string().min(1),
  postcode: z.string().max(7).nullable(),
  plaats: z.string().min(1),

  vraagprijs: z.number().int().nonnegative(),
  /** 'k.k.' or 'v.o.n.' in practice, but free text: a next source may differ. */
  prijsConditie: z.string().nullable(),
  oppervlakteM2: z.number().int().positive().nullable(),
  /** Generated column: vraagprijs / oppervlakteM2, null when the area is unknown. */
  prijsPerM2: z.number().int().nonnegative().nullable(),
  kamers: z.number().int().nonnegative().nullable(),
  energielabel: energielabelSchema.nullable(),

  makelaar: z.string().nullable(),
  makelaarUrl: z.url().nullable(),
  badge: z.string().nullable(),
  pitch: z.string().nullable(),

  lat: z.number().min(-90).max(90).nullable(),
  lng: z.number().min(-180).max(180).nullable(),

  /** Ons oordeel. `nieuw` zolang er niemand naar gekeken heeft. */
  beoordeling: beoordelingSchema,
  /** Wanneer dat oordeel voor het laatst is gezet; null zolang het `nieuw` is. */
  beoordeeldOp: isoDatumTijdSchema.nullable(),
  /** Hoe ver we met een kansje zijn (MA-51); null bij elk ander oordeel. */
  fase: faseSchema.nullable(),
  /** Sinds wanneer hij in deze fase staat; null zonder fase. */
  faseOp: isoDatumTijdSchema.nullable(),
  /**
   * Plaats binnen zijn kolom op het Kanban-bord, oplopend: laag staat bovenaan. Een
   * kommagetal, zodat een kaartje tussen twee andere past zonder de rest te
   * hernummeren. Null zonder fase.
   */
  faseVolgorde: z.number().nullable(),
  /**
   * De foto die wij de beste vinden (MA-51), als volgnummer in de fotoreeks; null
   * zolang niemand er een koos, dan begint de reeks bij de eerste van de bron.
   */
  hoofdfoto: z.number().int().nonnegative().nullable(),

  /* --- Advertentielogboek (MA-22) --- */
  /** Wat de laatste volledige ronde zag: actief of verdwenen. */
  status: woningstatusSchema,
  /** De stand voor het scherm: te koop, verkocht of uit het aanbod. Zie aanbod.ts. */
  aanbod: aanbodSchema,
  laatsteWaarnemingOp: isoDatumTijdSchema,
  /** Wanneer hij het laatst wegbleef; null zolang hij te koop staat. */
  verdwenenOp: isoDatumTijdSchema.nullable(),
  /** Hoe vaak hij uit het aanbod ging en terugkwam. Boven nul: "eerder actief geweest". */
  terugkomsten: z.number().int().nonnegative(),
  laatstTerugOp: isoDatumTijdSchema.nullable(),

  /* --- Opnieuw aangeboden (MA-45) --- */
  /** Een eerdere advertentie van dezelfde woning ging hieraan vooraf. */
  opnieuwAangeboden: z.boolean(),
  /** De vraagprijs van die vorige advertentie; null als er geen was. */
  vorigeVraagprijs: z.number().int().nonnegative().nullable(),

  aangemaaktOp: isoDatumTijdSchema,
  bijgewerktOp: isoDatumTijdSchema,
});
export type Listing = z.infer<typeof listingSchema>;

/**
 * One point on the map: a GeoJSON Feature whose properties carry everything the
 * balloon and its popup show, and nothing else.
 *
 * `postcode`, `badge` en `woningtype` staan er niet voor de popup maar voor het
 * weergavepaneel: de tekst onder de ballon per buurt (de PC4 van de postcode),
 * de markering "nieuw" (het merkje dat de bron zelf meegeeft) en de kleur per
 * woningtype. Alle drie stonden uitgeschakeld met de reden "de kaart krijgt dat
 * niet mee" - terwijl de data het wel had en alleen deze regels ontbraken.
 */
export const woningFeatureSchema = z.object({
  type: z.literal('Feature'),
  id: z.string(),
  geometry: z.object({
    type: z.literal('Point'),
    coordinates: z.tuple([z.number(), z.number()]),
  }),
  properties: z.object({
    id: z.uuid(),
    adres: z.string(),
    vraagprijs: z.number().int(),
    oppervlakteM2: z.number().int().nullable(),
    prijsPerM2: z.number().int().nullable(),
    energielabel: energielabelSchema.nullable(),
    kamers: z.number().int().nullable(),
    foto: z.string().nullable(),
    bronUrl: z.string().nullable(),
    postcode: z.string().nullable(),
    badge: z.string().nullable(),
    /** Afgeleid uit de detail-URL, zie `woningtypeUitBronUrl`; null als die er niet is. */
    woningtype: z.string().nullable(),
    /**
     * Ons oordeel, zodat de popup de keurknoppen in de juiste stand kan tonen
     * zonder de woning apart op te halen - en zodat het Kansjes-scherm zijn
     * eigen kaartje uit dezelfde GeoJSON kan tekenen.
     */
    beoordeling: beoordelingSchema,
    /** Te koop, verkocht of uit het aanbod - voor de markering op de kaart. */
    aanbod: aanbodSchema,
    /** Hoe vaak hij terugkwam; boven nul draagt de ballon "eerder actief geweest". */
    terugkomsten: z.number().int().nonnegative(),
    /** Opnieuw aangeboden (MA-45): de ballon draagt dan een ↻. */
    opnieuwAangeboden: z.boolean(),
    /** De vraagprijs van de vorige advertentie, voor de popup; null als er geen was. */
    vorigeVraagprijs: z.number().int().nonnegative().nullable(),
    /** De gekozen hoofdfoto (MA-51), als volgnummer; het popupje begint daar. */
    hoofdfoto: z.number().int().nonnegative().nullable(),
  }),
});
export type WoningFeature = z.infer<typeof woningFeatureSchema>;

export const woningFeatureCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(woningFeatureSchema),
});
export type WoningFeatureCollection = z.infer<typeof woningFeatureCollectionSchema>;
